import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Carga packs y tarifas de la tienda actual.
 * @returns {{ packs: Array, tariffs: Array }}
 */
export const loadPacksAndTariffs = async () => {
  try {
    const [packsRes, tariffsRes] = await Promise.all([
      axios.get(`${API}/packs`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      }),
      axios.get(`${API}/tariffs`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      }),
    ]);
    return {
      packs: packsRes.data || [],
      tariffs: tariffsRes.data || [],
    };
  } catch (error) {
    console.error("Error cargando packs/tarifas:", error);
    return { packs: [], tariffs: [] };
  }
};

/**
 * Obtiene el precio de un pack para un número de días dado.
 */
export const getPackPrice = (pack, days) => {
  if (!pack || !days || days <= 0) return 0;
  if (days <= 10 && pack[`day_${days}`]) return pack[`day_${days}`];
  if (days > 10 && pack.day_11_plus) {
    const base10 = pack.day_10 || pack.day_9 || pack.day_8 || pack.day_7 || 0;
    return base10 + (days - 10) * pack.day_11_plus;
  }
  return pack.day_1 || 0;
};

/**
 * Obtiene el precio de una tarifa individual para un tipo de item y días dados.
 * Para días > 10: base en día 10 + días adicionales × tarifa día 11+
 */
const getTariffPrice = (tariffs, itemType, days) => {
  const tariff = (tariffs || []).find(
    (t) => t.item_type?.toLowerCase() === itemType?.toLowerCase()
  );
  if (!tariff) return 0;
  if (days <= 10) return tariff[`day_${days}`] || tariff.day_1 || 0;
  // Para días > 10: igual que getPackPrice (base + días_extra × tarifa adicional)
  const base10 =
    tariff.day_10 || tariff.day_9 || tariff.day_8 || tariff.day_7 ||
    tariff.day_6 || tariff.day_5 || tariff.day_4 || tariff.day_3 ||
    tariff.day_2 || tariff.day_1 || 0;
  return base10 + (days - 10) * (tariff.day_11_plus || 0);
};

/**
 * Calcula el precio correcto para un conjunto de items y un número de días,
 * usando la tabla de precios escalonada de packs y tarifas.
 *
 * @param {string[]} itemTypes - Array de tipos de items del alquiler
 * @param {number} days - Número de días a calcular
 * @param {Array} packs - Packs disponibles de la tienda
 * @param {Array} tariffs - Tarifas individuales de la tienda
 * @returns {number} Precio total calculado
 */
export const calculateTariffPrice = (itemTypes, days, packs, tariffs) => {
  if (!days || days <= 0 || !itemTypes || itemTypes.length === 0) return 0;

  const normalizedTypes = itemTypes.map((t) => t?.toLowerCase());

  // Contar cuántos items de cada tipo hay
  const typeCounts = {};
  normalizedTypes.forEach((t) => {
    typeCounts[t] = (typeCounts[t] || 0) + 1;
  });

  let totalPackPrice = 0;
  const usedTypes = {};

  // Intentar asignar items a packs (packs más grandes primero)
  if (packs && packs.length > 0) {
    const sortedPacks = [...packs].sort(
      (a, b) => (b.items?.length || 0) - (a.items?.length || 0)
    );

    for (const pack of sortedPacks) {
      if (!pack.items || pack.items.length === 0) continue;

      const requiredCounts = {};
      pack.items.forEach((t) => {
        requiredCounts[t.toLowerCase()] =
          (requiredCounts[t.toLowerCase()] || 0) + 1;
      });

      // Formar tantas instancias del pack como sea posible
      let canForm = true;
      while (canForm) {
        for (const [type, required] of Object.entries(requiredCounts)) {
          const available =
            (typeCounts[type] || 0) - (usedTypes[type] || 0);
          if (available < required) {
            canForm = false;
            break;
          }
        }
        if (canForm) {
          totalPackPrice += getPackPrice(pack, days);
          for (const [type, required] of Object.entries(requiredCounts)) {
            usedTypes[type] = (usedTypes[type] || 0) + required;
          }
        }
      }
    }
  }

  // Items sueltos (no cubiertos por packs)
  let loosePrice = 0;
  for (const [type, count] of Object.entries(typeCounts)) {
    const usedCount = usedTypes[type] || 0;
    const remaining = count - usedCount;
    if (remaining > 0) {
      loosePrice += remaining * getTariffPrice(tariffs, type, days);
    }
  }

  return totalPackPrice + loosePrice;
};
